import { useEffect, useMemo, useState } from 'react';
import { FiUpload } from 'react-icons/fi';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { generateUploadSignedUrl } from 'api/internaltools';
import { createKycDetail } from 'api/kyc';
import { UPLOAD_FILE_ALLOWED_FORMATS } from 'constants/constants';
import {
  FILE_EXTENSION_TO_INPUT_FORMAT_MAPPING,
  FILE_EXTENSION_TO_TYPE_MAPPING,
  FILE_MIME_TYPE,
  FILE_SIZE,
  INPUT_FILE_FORMATS,
  KYC_DOCUMENT_KEYS,
  KYC_DOCUMENT_PARENT_KEYS,
  KYC_DOCUMENT_URL_KEY,
  SUBMIT_KYC_DOCUMENT_UPLOAD_ID,
} from 'modules/merchantinfo/constants/constants';

const ACCEPTED_UPLOAD_FORMATS = [
  INPUT_FILE_FORMATS.PNG,
  INPUT_FILE_FORMATS.JPEG,
  INPUT_FILE_FORMATS.JPG,
  INPUT_FILE_FORMATS.PDF,
  INPUT_FILE_FORMATS.BMP,
];

const DATE_PLACEHOLDER_KEYS = ['document_issued_date', 'document_expiry_date'];

const INITIAL_STATE = {
  entityId: '',
  parentKey: '',
  documentKey: '',
  textValue: '',
};

export default function SubmitKycDocumentModal({ isOpen, onClose, merchantId, entities = [] }) {
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadIdentifier, setUploadIdentifier] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const entityOptions = useMemo(() => entities.filter((entity) => entity?.kyb_id), [entities]);
  const selectedEntity = useMemo(() => entityOptions.find((entity) => entity.entity_id === formData.entityId), [
    entityOptions,
    formData.entityId,
  ]);

  const isUrlKey = formData.documentKey === KYC_DOCUMENT_URL_KEY;
  const resolvedValue = isUrlKey ? uploadIdentifier : formData.textValue.trim();
  const canUpload = Boolean(formData.entityId && formData.parentKey);
  const isFormValid = Boolean(formData.entityId && formData.parentKey && formData.documentKey && resolvedValue);

  const resetUploadState = () => {
    setUploadedFileName('');
    setUploadIdentifier('');
    setUploadError(null);
  };

  const handleClose = () => {
    setFormData(INITIAL_STATE);
    resetUploadState();
    setIsUploading(false);
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    resetUploadState();
  }, [formData.entityId, formData.parentKey, formData.documentKey]);

  const handleUploadButtonClick = () => {
    document.getElementById(SUBMIT_KYC_DOCUMENT_UPLOAD_ID)?.click();
  };

  const handleFileChange = async (file) => {
    if (!file) return;

    setUploadError(null);

    if (!canUpload) {
      setUploadError('* Select an entity and parent key before uploading.');

      return;
    }

    if (file.size > FILE_SIZE.TWO_MB) {
      setUploadError(`* File size cannot exceed ${FILE_SIZE.TWO_MB / FILE_SIZE.ONE_MB}MB.`);

      return;
    }

    const fileExtension = file?.name?.split('.')?.pop() ?? '';
    const isAllowedFormat = ACCEPTED_UPLOAD_FORMATS.includes(FILE_EXTENSION_TO_INPUT_FORMAT_MAPPING[fileExtension]);

    if (!isAllowedFormat) {
      setUploadError('* Invalid file format.');

      return;
    }

    setIsUploading(true);

    try {
      const normalizedExtension = FILE_MIME_TYPE[file?.type] ?? fileExtension;
      const key = `${merchantId}_${Date.now()}.${normalizedExtension}`;

      const response = await generateUploadSignedUrl({
        fileExtension: normalizedExtension,
        key,
        kybId: selectedEntity?.kyb_id,
        documentKey: formData.parentKey,
        isBusinessDocument: true,
        companyOwner: '',
      });

      const { identifier, url } = response?.data?.data || {};

      const headers = new Headers();

      headers.append('Content-Type', file?.type || FILE_EXTENSION_TO_TYPE_MAPPING[fileExtension]);

      const uploadResponse = await fetch(url, { method: 'PUT', headers, body: file });

      if (uploadResponse?.status !== 200) {
        setUploadError(`* Upload failed: ${uploadResponse?.statusText || 'please try again.'}`);

        return;
      }

      setUploadedFileName(file?.name);
      setUploadIdentifier(identifier);
    } catch (err) {
      setUploadError(`* Error: ${err?.response?.data?.message || err?.message || 'failed to upload document.'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast({
        title: isUrlKey ? 'Please complete all fields and upload a document.' : 'Please fill all fields.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });

      return;
    }

    try {
      setIsSubmitting(true);
      await createKycDetail(formData.entityId, {
        parent_key: formData.parentKey,
        key: formData.documentKey,
        value: resolvedValue,
      });
      toast({
        title: 'KYC document details submitted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handleClose();
    } catch (error) {
      toast({
        title: 'Failed to submit KYC document details.',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size='lg' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Submit Documents</ModalHeader>
        <ModalCloseButton />
        <ModalBody px={8}>
          <VStack spacing={4} align='stretch'>
            <FormControl isRequired>
              <FormLabel>Entity</FormLabel>
              <Select
                placeholder='Select Entity'
                value={formData.entityId}
                onChange={(e) => handleChange('entityId', e.target.value)}
              >
                {entityOptions.map((entity) => (
                  <option key={entity.entity_id} value={entity.entity_id}>
                    {entity.entity_name || entity.entity_id}
                  </option>
                ))}
              </Select>
              {entityOptions.length === 0 && (
                <Text color='gray.500' fontSize='sm' mt={2}>
                  No entities with a KYB id available for this merchant.
                </Text>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Parent Key</FormLabel>
              <Select
                placeholder='Select Parent Key'
                value={formData.parentKey}
                onChange={(e) => handleChange('parentKey', e.target.value)}
              >
                {KYC_DOCUMENT_PARENT_KEYS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Key</FormLabel>
              <Select
                placeholder='Select Key'
                value={formData.documentKey}
                onChange={(e) => handleChange('documentKey', e.target.value)}
              >
                {KYC_DOCUMENT_KEYS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {formData.documentKey &&
              (isUrlKey ? (
                <FormControl isRequired>
                  <FormLabel>Document</FormLabel>
                  <Flex
                    direction='column'
                    align='center'
                    gap={2}
                    p={6}
                    borderWidth='1px'
                    borderRadius='6px'
                    bg='rgba(240,248,255,0.5)'
                  >
                    {uploadIdentifier && (
                      <Text isTruncated color='gray.600' width='100%' textAlign='center'>
                        📄 {uploadedFileName || uploadIdentifier}
                      </Text>
                    )}
                    <Button
                      leftIcon={<FiUpload />}
                      width='fit-content'
                      borderRadius='20px'
                      borderWidth='2px'
                      isDisabled={!canUpload || isUploading}
                      isLoading={isUploading}
                      loadingText='Uploading'
                      onClick={handleUploadButtonClick}
                    >
                      {uploadIdentifier ? 'Replace Document' : 'Upload Document'}
                    </Button>
                    <input
                      id={SUBMIT_KYC_DOCUMENT_UPLOAD_ID}
                      type='file'
                      accept={UPLOAD_FILE_ALLOWED_FORMATS}
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(e.target.files[0])}
                    />
                    <Text fontSize='xs' color='gray.500'>
                      Supported: PNG, JPEG, JPG, PDF, BMP · max 20MB
                    </Text>
                  </Flex>
                  {!canUpload && (
                    <Text color='gray.500' fontSize='sm' mt={2}>
                      Select an entity and parent key to enable upload.
                    </Text>
                  )}
                  {uploadError && (
                    <Text color='red.500' fontSize='sm' mt={2}>
                      {uploadError}
                    </Text>
                  )}
                </FormControl>
              ) : (
                <FormControl isRequired>
                  <FormLabel>Value</FormLabel>
                  <Input
                    placeholder={
                      DATE_PLACEHOLDER_KEYS.includes(formData.documentKey) ? 'e.g. YYYY-MM-DD' : 'Enter value'
                    }
                    value={formData.textValue}
                    onChange={(e) => handleChange('textValue', e.target.value)}
                  />
                </FormControl>
              ))}
          </VStack>
        </ModalBody>
        <ModalFooter px={8}>
          <Button variant='ghost' mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button colorScheme='blue' onClick={handleSubmit} isLoading={isSubmitting} isDisabled={!isFormValid}>
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
